import { useMemo } from 'react';

import { CELL_GROUP_HEIGHT_PX } from '../../constants';
import { calculateAssignmentLevels } from '../../helpers';
import { getTimelineRowHeight } from '../../helpers/planner-base/timeline-row-height';
import usePlannerStore from '../../store';
import { AssignmentType, RowItem, RowItemType } from '../../types';

/**
 * Calculates heights for all rows based on their assignment levels and visible user info fields.
 * Recomputes when userInfoVisibility changes so the virtualizer always has correct estimates.
 *
 * Time-off never grows the row — a time-off block always fits within the existing
 * content area (sized as a portion of it), and a partial time-off repositions the
 * assignment into the free zone beside the block. So the row height depends only
 * on assignment levels and the sidebar header, matching TimelineRow's own cellHeight.
 */
export const useRowHeights = (
  rows: RowItem[],
  assignmentsMap: Map<string, AssignmentType[]>
): Map<string, number> => {
  const userInfoVisibility = usePlannerStore(store => store.userInfoVisibility);

  return useMemo(() => {
    const heights = new Map<string, number>();

    rows.forEach(row => {
      const key = row.parentGroupId ? `${row.parentGroupId}-${row.id}` : row.id;

      if (row.type === RowItemType.Group) {
        heights.set(key, CELL_GROUP_HEIGHT_PX);
      } else {
        const assignments = assignmentsMap.get(row.id);
        const { maxIntersectedAssignments } = assignments?.length
          ? calculateAssignmentLevels(assignments)
          : { maxIntersectedAssignments: 1 };
        heights.set(key, getTimelineRowHeight({ maxIntersectedAssignments, userInfoVisibility }));
      }
    });

    return heights;
  }, [rows, assignmentsMap, userInfoVisibility]);
};
